import * as React from 'react';
import { useState } from 'react';

/** Material UI Packages. */
import { Dialog, Typography, Box, OutlinedInput } from '@mui/material';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setFormData } from 'shared/store/slices/filterViewListSlice';
import { setFormData as setFilterFormData } from 'shared/store/slices/filterViewSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import { createSocketData } from 'shared/lib/fetchSocket';

const MAX_FILTER_LENGTH = 49;

export default function FilterViewDialog(props) {
    /** Get props from FilterView.js */
    const { open, setOpen, setSelectedValue } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** State to manage the filter values applied to the current DataGrid. */
    const filterView = useSelector((state) => state.filterViewData);

    /** State to manage the name of the newly created filterView. */
    const [filterViewName, setFilterViewName] = useState('');

    /** Handle to close a dialog. */
    const handleClose = () => {
        setOpen(false);
        setFilterViewName('');
    };

    /** Handle to change filterView name. */
    const handleNameChange = (event) => {
        setFilterViewName(event.target.value);
    };

    /** Validate filter data length */
    const validateFilterLength = (filterData) => {
        const nameLength = filterData.name ? filterData.name.length : 0;

        if (nameLength > MAX_FILTER_LENGTH) {
            const excessLength = nameLength - MAX_FILTER_LENGTH;
            dispatch(
                setSnackbar({
                    open: true,
                    message: `Filter name exceeds limit by ${excessLength} characters (${nameLength}/${MAX_FILTER_LENGTH}). Please shorten the filter name.`,
                    severity: 'warning',
                })
            );
            return false;
        } else if (nameLength === 0) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please fill out filter view name.',
                    severity: 'warning',
                })
            );
            return false;
        }
        return true;
    };

    /** Handle successful filter creation */
    const handleCreateSuccess = (receivedData) => {
        dispatch(
            setSnackbar({
                open: true,
                message: 'New filter has been successfully created.',
                severity: 'success',
            })
        );

        setFilterViewName('');
        setOpen(false);

        const updatedFilterViewList = receivedData;
        dispatch(setFormData(updatedFilterViewList));

        const lastObject = updatedFilterViewList[updatedFilterViewList.length - 1];
        dispatch(setFilterFormData(lastObject));
        setSelectedValue(lastObject.id);
    };

    /** Handle to submit new filter value to server and applyto current table. 7*/
    const handleSubmit = () => {
        const filterViewValue = {
            shipment_type: tabValue,
            name: filterViewName,
            filterModel: filterView.filterModel,
            sortModel: filterView.sortModel,
            columnVisibilityModel: filterView.columnVisibilityModel,
        };

        // Validate filter data length
        if (!validateFilterLength(filterViewValue)) {
            return;
        }
        createSocketData(dispatch, tabValue, 'row', 'il_filter', '', filterViewValue, (response) => {
            handleCreateSuccess(response.data);
        });
    };

    return (
        <Dialog
            maxWidth="sm"
            fullWidth={true}
            open={open}
            onClose={handleClose}
            PaperComponent={PaperComponent}
            PaperProps={{
                sx: {
                    borderRadius: '18px',
                },
            }}
        >
            <Box sx={{ height: 180, display: 'flex', flexDirection: 'column' }}>
                <DialogHeader cursor title={'SAVE YOUR FILTER VIEW NOW?'} onClose={handleClose} onSave={handleSubmit} />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                    }}
                >
                    <Typography sx={{ display: 'inline', color: '#003a52', fontWeight: 500, mr: '15px' }}>
                        Filter View Name
                    </Typography>
                    <OutlinedInput
                        size="small"
                        sx={{ width: '60%' }}
                        id="filterViewName"
                        name="filterViewName"
                        value={filterViewName}
                        onChange={handleNameChange}
                    />
                </Box>
            </Box>
        </Dialog>
    );
}
